import { ThemeConfig } from 'react-select/src/theme';

import fonts from '@legacy/styles/fonts';
import colors from '@legacy/moon/particles/colors';
import originalColors from '@legacy/styles/colors';

const lightColors = (theme, isCompact) => ({
  ...theme.colors,
  background: {
    default: colors.light,
    error: `linear-gradient(180deg, ${colors.light} 0%, ${colors.halo} 100%)`,
  },
  controlBorder: {
    default: colors.titanium,
    hover: colors.steel,
    focus: colors.echo,
    shadow: colors.silver,
    disabled: colors.titanium,
    error: colors.mars,
  },
  indicator: {
    default: isCompact ? colors.moonlight60 : colors.blackhole,
    disabled: colors.silver,
  },
  menu: {
    shadow: isCompact ? 'none' : colors.aluminium,
    border: isCompact ? colors.moonlight30 : colors.aluminium,
  },
  group: {
    default: colors.silver,
  },
  option: {
    default: isCompact ? colors.moonlight60 : colors.steel,
    selected: colors.blackhole,
    hover: colors.blackhole,
  },
  optionBackground: {
    default: 'transparent',
    selected: isCompact ? colors.moonlight20 : colors.stardust,
    hover: isCompact ? colors.moonlight20 : colors.aluminium,
  },
  placeholder: {
    default: colors.selenium,
    disabled: colors.silver,
    error: colors.caliste,
  },
  singleValue: {
    default: isCompact ? colors.moonlight60 : colors.darkside,
    disabled: colors.steel,
  },
  instructions: {
    default: colors.selenium,
    disabled: colors.silver,
    error: colors.caliste,
  },

  multiValueLabel: {
    default: colors.stardust,
    error: colors.nebula,
  },
  multiValueRemove: {
    default: colors.blackhole,
    disabled: colors.silver,
  },
});

const darkColors = (theme, alternate) => ({
  ...theme.colors,
  background: {
    default: alternate ? colors.moonlight90 : colors.moonlight100,
    error: `linear-gradient(180deg, ${alternate ? colors.moonlight90 : colors.moonlight100} 0%, ${
      colors.mars50
    } 69.79%)`,
  },
  controlBorder: {
    default: colors.selenium,
    hover: colors.aluminium,
    focus: colors.borealis50,
    shadow: colors.selenium,
    disabled: colors.selenium,
    error: colors.mars50,
  },
  indicator: {
    default: colors.light,
    disabled: colors.selenium,
  },
  menu: {
    shadow: colors.selenium,
    border: colors.darkside,
  },
  group: {
    default: colors.selenium,
  },
  option: {
    default: colors.silver,
    selected: colors.fullmoon,
    hover: alternate ? colors.moonlight80 : colors.moonlight90,
  },
  optionBackground: {
    default: 'transparent',
    selected: colors.borealis90,
    hover: colors.moonlight80,
  },
  placeholder: {
    default: colors.silver,
    disabled: colors.selenium,
    error: colors.mars,
  },
  singleValue: {
    default: colors.light,
    disabled: colors.steel,
  },
  instructions: {
    default: colors.titanium,
    disabled: colors.selenium,
    error: colors.mars,
  },
  multiValueLabel: {
    default: colors.gemini,
    error: colors.mars,
  },
  multiValueRemove: {
    default: colors.light,
    disabled: colors.selenium,
  },
});

const alphaColors = theme => ({
  ...theme.colors,
  background: {
    default: colors.moonlight90,
    error: colors.darkside,
  },
  controlBorder: {
    default: colors.itrium,
    hover: colors.silver,
    focus: colors.echo,
    error: colors.mars,
    disabled: colors.selenium,
  },
  indicator: {
    default: colors.silver,
    disabled: colors.itrium,
  },
  menu: {
    shadow: 'none',
    border: colors.itrium,
  },
  group: {
    default: colors.aluminium,
  },
  option: {
    default: colors.silver,
    selected: colors.fullmoon,
    hover: colors.darkside,
  },
  optionBackground: {
    default: 'transparent',
    selected: colors.gemini,
    hover: colors.blackhole,
  },
  placeholder: {
    default: colors.silver,
    error: colors.aluminium,
    disabled: colors.selenium,
  },
  singleValue: {
    default: colors.aluminium,
    disabled: colors.selenium,
  },
  instructions: {
    default: colors.titanium,
    disabled: colors.selenium,
    error: colors.mars,
  },
  multiValueLabel: {
    default: colors.gemini,
    error: colors.mars,
  },
  multiValueRemove: {
    default: colors.light,
    disabled: colors.selenium,
  },
});

const compactSelect = () => ({
  borderRadius: '20px',
  border: `1px solid ${colors.moonlight30}`,
  fontSize: '12px',
  minHeight: '20px !important',
  boxShadow: 'none',
  '&:hover': {
    border: `1px solid ${colors.moonlight30}`,
    boxShadow: `none`,
  },
  '&:focus': {
    border: `1px solid ${colors.moonlight30}`,
    boxShadow: `none`,
  },
});

const getTheme = (theme, product, isCompact, alternate) => {
  switch (product) {
    case 'dark':
      return darkColors(theme, alternate);
    case 'alpha':
      return alphaColors(theme);
    case 'light':
    default:
      return lightColors(theme, isCompact);
  }
};

const getThemeSelect = (product, isCompact) => {
  switch (product) {
    case 'light':
      if (isCompact) return compactSelect();
      return {};
    default:
      return {};
  }
};

const nvioOptions = () => ({
  borderRadius: 'none',
  borderBottom: `1px solid ${originalColors.sea}`,
});

const compactOptions = () => ({
  padding: '2px 8px',
  paddingLeft: '8px',
  fontSize: '12px',
  borderRadius: 'none',
  borderBottom: `1px solid ${colors.moonlight30}`,
  '&:last-child': {
    border: 'unset',
  },
});

const getThemeOption = (product, isCompact) => {
  switch (product) {
    case 'nvio':
      return nvioOptions();
    case 'light':
      if (isCompact) return compactOptions();
      return {};
    default:
      return {};
  }
};

const nvioMenuList = () => ({
  padding: 0,
});

const compactMenuList = () => ({
  borderRadius: '6px',
  padding: 0,
});

const getThemeMenuList = (product, isCompact) => {
  switch (product) {
    case 'nvio':
      return nvioMenuList();
    case 'light':
      if (isCompact) return compactMenuList();
      return {};
    default:
      return {};
  }
};

const compactGroup = () => ({
  '&:first-child': {
    borderColor: '#000 !important',
  },
});

const getThemeGroup = (product, isCompact) => {
  switch (product) {
    case 'light':
      if (isCompact) return compactGroup();
      return {};
    default:
      return {};
  }
};

const compactSingleValue = () => ({
  top: '50%',
  bottom: '',
  marginLeft: '10px',
});

const getThemeSingleValue = (product, isCompact) => {
  switch (product) {
    case 'light':
      if (isCompact) return compactSingleValue();
      return {};
    default:
      return {};
  }
};

const compactValueContainer = () => ({
  height: '20px',
  maxWidth: '100%',
});

const getThemeValueContainer = (product, isCompact) => {
  switch (product) {
    case 'light':
      if (isCompact) return compactValueContainer();
      return {};
    default:
      return {};
  }
};

const compactDropdownIcon = () => ({
  top: '7px',
  right: '8px',
});

const getThemeDropdownIcon = (product, isCompact) => {
  switch (product) {
    case 'light':
      if (isCompact) return compactDropdownIcon();
      return {};
    default:
      return {};
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default (theme: ThemeConfig, product: string, isCompact: boolean, alternate?: boolean): any => ({
  borderRadius: isCompact ? 6 : 8,
  fontFamily: fonts.bitso,
  colors: getTheme(theme, product, isCompact, alternate),
  customSelect: getThemeSelect(product, isCompact),
  customOption: getThemeOption(product, isCompact),
  customMenuList: getThemeMenuList(product, isCompact),
  customGroup: getThemeGroup(product, isCompact),
  customSingleValue: getThemeSingleValue(product, isCompact),
  customValueContainer: getThemeValueContainer(product, isCompact),
  customDropdownIcon: getThemeDropdownIcon(product, isCompact),
  product,
  isCompact,
  spacing: {
    ...theme.spacing,
    controlHeight: isCompact ? 20 : 48,
    menuGutter: 4,
    baseUnit: isCompact ? 0 : 6,
  },
});
